import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { AJUSTE_YA_EN_MARCHA, abrirPagoDelAjuste } from '../lib/revision';
import { Pill, Clock, Beaker, Link2, CalendarClock, StickyNote, ChevronDown, Copy, Check, ExternalLink } from 'lucide-react';

// EL ASPECTO ES EL DEL DISEÑO DEL 25-10 (docs/diseño/suplementos): las clases `sup-*` viven
// en index.css. Lo que se pide y lo que se pinta no cambia; cambia cómo se ve.
// `mia`: la pantalla de quien ya tiene su pauta, con las medidas de su diseño.
const Wrap = ({ children, mia = false }) => (
    <div className={`sup${mia ? ' sup-mia' : ''} animate-fade-in`} data-testid="supplements-page">{children}</div>
);

// La cabecera del protocolo y del cartel vacío. El título sigue diciendo de quién es
// (punto 180): «Mis suplementos» cuando es su protocolo, «Suplementación» si no tiene plan.
const Cabecera = ({ conPlan }) => (
    <header className="flex flex-col gap-2">
        <span className="sup-ceja">Mis suplementos</span>
        <h1 className="sup-titulo">{conPlan ? 'Mis suplementos' : 'Suplementación'}</h1>
    </header>
);

// LA CABECERA DE LA GUÍA (25-10): ceja, título y el texto de Jesús, tal cual, justo antes de
// las categorías. Va escrito aquí y no sale de `texto_entrada`: es el texto que pidió para
// esta pantalla. Sin el párrafo del Catálogo Premium (punto 182, confirmado el 25-10).
const ENTRADA_DE_LA_GUIA = [
    'Estos son los suplementos que más utilizo y recomiendo, con pautas exactas sobre su uso. '
        + 'Están organizados por categorías según su función. En condiciones normales, te '
        + 'recomiendo empezar con los básicos.',
];
const CabeceraDeLaGuia = () => (
    <header className="flex flex-col gap-2">
        <span className="sup-ceja">Mis suplementos</span>
        <h1 className="sup-titulo">Guía básica de suplementación</h1>
        <div className="flex flex-col gap-3 mt-1" data-testid="texto-entrada-guia">
            {ENTRADA_DE_LA_GUIA.map((p, i) => <p key={i} className="sup-entrada">{p}</p>)}
        </div>
    </header>
);

// LOS ENLACES DE COMPRA, COMO PÍLDORAS (25-10): «Comprar con descuento en FullGas →». El
// texto lo sigue componiendo `textosDeLosEnlaces`, que es lo que vigilan los tests.
const BotonesDeCompra = ({ enlaces, titulo }) => (
    <div className="flex flex-wrap gap-2 mt-0.5">
        {textosDeLosEnlaces(enlaces, titulo).map((texto, i) => (
            <a key={i} href={urlDeEnlace(enlaces[i])} target="_blank" rel="noopener noreferrer"
                className="sup-comprar">
                {texto} <span aria-hidden="true">→</span>
            </a>
        ))}
    </div>
);

// Cuándo y Dosis como lista de definición, en el orden que se le pase.
const Pautas = ({ filas }) => {
    const conValor = filas.filter(([, v]) => v);
    if (!conValor.length) return null;
    return (
        <dl className="sup-dl">
            {conValor.map(([etiqueta, valor]) => (
                <React.Fragment key={etiqueta}>
                    <dt>{etiqueta}</dt>
                    <dd>{valor}</dd>
                </React.Fragment>
            ))}
        </dl>
    );
};

const formatDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
};

// EL ENLACE DICE A DÓNDE VA (Gonzalo, 4-09, punto 84). «Comprar con descuento 1» y «Comprar
// con descuento 2» no le dicen al cliente nada de lo que va a abrir. El catálogo solo guarda
// la URL, así que el nombre de la tienda sale del dominio: sin «www.» ni terminación y con
// la primera en mayúscula («Comprar en Bulevip»). Las tiendas cuya marca se escribe de otra
// forma van en esta lista, que es corta a propósito: son las que hay en el catálogo.
const MARCA_DE_LA_TIENDA = {
    fullgas: 'FullGas', hsn: 'HSN', amzn: 'Amazon', amazon: 'Amazon', iherb: 'iHerb',
    biotechusa: 'BioTechUSA', masmusculo: 'MASmusculo', nootropicsdepot: 'Nootropics Depot',
    primemale: 'Prime Male', tantrumcbd: 'Tantrum CBD', openfoodfacts: 'Open Food Facts',
    myprotein: 'Myprotein', prozis: 'Prozis', decathlon: 'Decathlon', ostrovit: 'OstroVit',
    bulevip: 'Bulevip', bodyshock: 'Bodyshock', traumaceutics: 'Traumaceutics',
    carmencita: 'Carmencita', vademecum: 'Vademecum',
};
// Y NO TODO LO ENLAZADO SE COMPRA: el Ursobilane apunta a su prospecto en Vademecum y la sal
// yodada a su ficha en Open Food Facts. A eso no se le dice «Comprar»: se abre para leerlo.
const NO_SON_TIENDAS = ['Vademecum', 'Open Food Facts'];
export const nombreDeLaTienda = (url) => {
    let host = '';
    try { host = new URL(String(url || '')).hostname.toLowerCase(); } catch { return ''; }
    // Fuera los prefijos que no son la marca («www.», «shop.», «es.») y la terminación.
    const trozos = host.split('.').filter(Boolean);
    while (trozos.length > 2 && ['www', 'shop', 'es', 'en', 'm', 'tienda', 'store'].includes(trozos[0])) trozos.shift();
    const base = trozos.length > 1 ? trozos[trozos.length - 2] : trozos[0] || '';
    if (!base) return '';
    return MARCA_DE_LA_TIENDA[base] || base.charAt(0).toUpperCase() + base.slice(1);
};
// LA DIRECCIÓN DE UN ENLACE, venga como venga: los protocolos viejos guardaron la URL
// suelta y los bloques del coach guardan `{label, url}`. Aquí ya se resolvía a mano en
// cuatro sitios; ahora hay uno.
const urlDeEnlace = (enlace) => (typeof enlace === 'string' ? enlace : enlace?.url) || '';

// «Comprar en FullGas»; y si el enlace trae su etiqueta (los bloques del coach la llevan:
// «Hydropeptides», «MAP»), «Hydropeptides · Comprar en FullGas», que es lo que distingue
// dos enlaces a la misma tienda.
const textoDelEnlace = (enlace) => {
    const url = urlDeEnlace(enlace);
    const tienda = nombreDeLaTienda(url);
    if (!tienda) return 'Comprar con descuento';
    // «COMPRAR CON DESCUENTO», SU LITERAL, Y LA TIENDA (Francisco, 17-09).
    //
    // Su artefacto cambia «Dónde encontrarlo» por «Comprar con descuento»: «Dónde
    // encontrarlo es vago, parece que te va a decir en qué tienda buscarlo y luego te lleva
    // a una web; Comprar con descuento pone por delante lo que gana él, y engancha con el
    // código de abajo, que es el mismo 20 %».
    //
    // «Dónde encontrarlo» ya no estaba, pero su literal tampoco: desde el punto 84 de
    // Gonzalo (4-09) el enlace decía «Comprar en FullGas», porque «Comprar con descuento 1»
    // y «2» no dicen a dónde llevan. Las dos cosas son ciertas y caben juntas, así que el
    // enlace lleva su frase entera y la tienda detrás. Lo que no se toca es lo que no se
    // compra: el prospecto del Ursobilane sigue siendo «Ver la ficha en Vademecum», que
    // llamar «comprar» a un prospecto sería peor que vago.
    const verbo = NO_SON_TIENDAS.includes(tienda) ? 'Ver la ficha en' : 'Comprar con descuento en';
    return enlace?.label ? `${enlace.label} · ${verbo} ${tienda}` : `${verbo} ${tienda}`;
};
// Y CUANDO EL COMBO LLEVA EL NOMBRE DE LOS DOS BOTES, SE USA (Gonzalo, 4-09, punto 84;
// cerrado el 13-09). El punto pedía «cada enlace con el nombre de su tienda» y eso ya estaba,
// pero los combos de los protocolos migrados seguían saliendo «Comprar en FullGas 1» y
// «Comprar en FullGas 2»: dos enlaces a la misma tienda, sin etiqueta, y un número que no
// dice cuál es cuál. El nombre de los dos botes SÍ está, en el título de la ficha: «Whey
// Isolate + crema de arroz», «Hydropeptides o MAP». Si el título se parte en tantos trozos
// como enlaces hay, cada uno se queda con el suyo y sale «Whey Isolate · Comprar en FullGas»,
// que es el mismo formato que ya usan los bloques del coach con su `label`.
//
// El número se queda como último respaldo: un combo cuyo título no se parta -- o que se parta
// en otra cantidad -- no puede quedarse con dos enlaces indistinguibles.
const TROZOS_DEL_TITULO = /\s+(?:\+|o|y)\s+/i;
const nombresDelCombo = (titulo, cuantos) => {
    const trozos = String(titulo || '').split(TROZOS_DEL_TITULO).map((t) => t.trim()).filter(Boolean);
    return trozos.length > 1 && trozos.length === cuantos ? trozos : null;
};
// Los enlaces de un suplemento, ya con su texto. Si dos caen en la misma tienda y no traen
// etiqueta (los protocolos viejos guardaron solo la URL) se distinguen por el trozo de título
// que les toca y, si no lo hay, se numeran. Con uno solo, nunca hay número.
export const textosDeLosEnlaces = (enlaces, titulo) => {
    const lista = enlaces || [];
    const nombres = nombresDelCombo(titulo, lista.length);
    const textos = lista.map((e, i) => (
        (nombres && !e?.label) ? textoDelEnlace({ ...(typeof e === 'string' ? { url: e } : e), label: nombres[i] })
            : textoDelEnlace(e)
    ));
    const cuantos = textos.reduce((acc, t) => ({ ...acc, [t]: (acc[t] || 0) + 1 }), {});
    const vistos = {};
    return textos.map((t) => {
        if (cuantos[t] < 2) return t;
        vistos[t] = (vistos[t] || 0) + 1;
        return `${t} ${vistos[t]}`;
    });
};

/**
 * LOS ENLACES DE SU PAUTA, COMO LOS PINTA EL DISEÑO DEL 25-10 (docs/diseño/suplementos 2):
 * «Comprar Amilopectina −20% ↗», y en un combo el nombre de cada bote («Whey Isolate −20%»,
 * «Crema de arroz −20%»). El «−20%» solo va donde vale el código, que es FullGas. Lo que no se
 * compra sigue diciendo «Ver la ficha en Vademecum», y una tienda que no es FullGas dice cuál
 * es («Comprar en Amazon»). Si aun así dos quedan iguales, se numeran, como en la guía.
 */
const conMayuscula = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
export const enlacesDeMiPauta = (enlaces, titulo) => {
    const lista = enlaces || [];
    const nombres = nombresDelCombo(titulo, lista.length);
    const enlacesConTexto = lista.map((e, i) => {
        const url = urlDeEnlace(e);
        const tienda = nombreDeLaTienda(url);
        if (NO_SON_TIENDAS.includes(tienda)) return { url, texto: `Ver la ficha en ${tienda}`, descuento: false };
        const esFullGas = tienda === 'FullGas';
        const nombre = e?.label || nombres?.[i];
        let texto;
        if (nombre) texto = conMayuscula(nombre);
        else if (esFullGas || !tienda) texto = titulo ? `Comprar ${titulo}` : 'Comprar con descuento';
        else texto = `Comprar en ${tienda}`;
        return { url, texto, descuento: esFullGas };
    });
    const cuantos = enlacesConTexto.reduce((acc, e) => ({ ...acc, [e.texto]: (acc[e.texto] || 0) + 1 }), {});
    const vistos = {};
    return enlacesConTexto.map((e) => {
        if (cuantos[e.texto] < 2) return e;
        vistos[e.texto] = (vistos[e.texto] || 0) + 1;
        return { ...e, texto: `${e.texto} ${vistos[e.texto]}` };
    });
};

/**
 * LAS FOTOS DE UNA FICHA, SIEMPRE COMO LISTA (Jesús, 6-09).
 *
 * Del combo de post-entreno pidió «la foto de la proteína y la crema de arroz debajo»: dos
 * botes, uno encima del otro. Hasta hoy cada ficha tenía UNA foto (`imagen`), así que se
 * añade `imagenes` sin tocar la de siempre: si la ficha no la trae, la lista es la de antes
 * con un solo elemento y la tarjeta se ve exactamente igual que ahora.
 *
 * Cada entrada puede ser la URL suelta o `{url, enlace, alt}`. Y una entrada SIN url es
 * legítima: es el bote del que todavía no tenemos foto -- sale con el icono de pastilla,
 * como cualquier ficha sin foto -- y sigue llevando a su página.
 */
const fotosDe = (item) => (item.imagenes?.length ? item.imagenes : [item.imagen])
    .map((f) => (f && typeof f === 'object'
        ? { url: f.url || '', enlace: f.enlace, alt: f.alt }
        : { url: f || '' }));

/**
 * LA COLUMNA DE FOTOS, Y EL BOTE SE PINCHA (Jesús, 6-09: «que se pueda hacer clic en el
 * bote y que el bote lleve a la página»).
 *
 * A dónde va cada bote: al enlace de la propia foto si lo trae y, si no, al primer enlace
 * de la ficha, que es el único destino que se puede dar por bueno cuando hay una sola.
 * Sin ninguna dirección la foto NO se envuelve en un enlace: un <a> sin destino se puede
 * pinchar y no lleva a ninguna parte, que es peor que no poder pincharlo.
 *
 * EL CONTROL DE ERROR ES POR FOTO. Antes había un `imgError` para la única que había; con
 * varias, una que no cargue no puede tumbar a las demás al icono de pastilla.
 */
const ColumnaDeFotos = ({ fotos, alt, enlacePorDefecto }) => {
    const [fallos, setFallos] = useState({});
    return (
        <div className="flex flex-col gap-2 min-w-0" data-testid="fotos-suplemento">
            {fotos.map((foto, i) => {
                const conFoto = foto.url && !fallos[i];
                const caja = (
                    <div className={`sup-foto${conFoto ? '' : ' vacia'}`}>
                        {conFoto
                            ? <img src={foto.url} alt={foto.alt || alt} loading="lazy"
                                onError={() => setFallos((previos) => ({ ...previos, [i]: true }))} />
                            : <Pill className="w-8 h-8" />}
                    </div>
                );
                const destino = urlDeEnlace(foto.enlace) || enlacePorDefecto || '';
                return destino
                    ? <a key={i} href={destino} target="_blank" rel="noopener noreferrer" className="block"
                        title={foto.alt || alt} data-testid="foto-suplemento-enlace">{caja}</a>
                    : <React.Fragment key={i}>{caja}</React.Fragment>;
            })}
        </div>
    );
};

/**
 * LA OTRA OPCIÓN, EN SU PROPIA LÍNEA (Jesús, 6-09): «quiero que ponga "Proteína
 * alternativa:" y salga esta [la de clara de huevo]».
 *
 * No es un enlace más de la fila de comprar: es una alternativa al bote de arriba, y lo que
 * la explica es su etiqueta. Por eso va aparte, con la etiqueta en negrita y dos puntos.
 * El texto del enlace se compone SIN la etiqueta («Comprar en FullGas» y no «Proteína
 * alternativa · Comprar en FullGas»), que ya la lleva delante.
 */
const EnlacesAlternativos = ({ enlaces }) => (
    <div className="space-y-1" data-testid="enlaces-alternativos">
        {enlaces.map((e, i) => (
            <div key={i} className="flex flex-wrap items-center gap-1.5 text-sm">
                <span className="font-semibold" style={{ color: 'var(--sup-valor)' }}>{e.label}:</span>
                <a href={urlDeEnlace(e)} target="_blank" rel="noopener noreferrer" className="sup-enlace">
                    {textoDelEnlace({ url: urlDeEnlace(e) })} <span aria-hidden="true">→</span>
                </a>
            </div>
        ))}
    </div>
);

const SupplementCard = ({ item }) => {
    const [imgError, setImgError] = useState(false);
    return (
    <div className="surface p-0 overflow-hidden flex flex-col sm:flex-row" data-testid="supplement-card">
        {/* En móvil la foto iba oculta y la tarjeta se quedaba en puro texto. Ahora
            sale también ahí, como una banda arriba, y en escritorio como columna. */}
        <div className="flex h-28 sm:h-auto sm:w-44 items-center justify-center bg-muted/40 p-3 flex-shrink-0">
            {item.imagen && !imgError ? (
                <img
                    src={item.imagen}
                    alt={item.titulo}
                    className="max-h-full max-w-full object-contain"
                    onError={() => setImgError(true)}
                />
            ) : (
                <Pill className="w-10 h-10 text-brand/50" />
            )}
        </div>
        <div className="flex-1 p-4">
            <h3 className="font-semibold text-foreground mb-2">{item.titulo}</h3>
            {item.cuando && (
                <p className="text-sm text-muted-foreground flex gap-2 mb-1">
                    <Clock className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                    <span><span className="font-semibold text-foreground">¿Cuándo? </span>{item.cuando}</span>
                </p>
            )}
            {item.cuanto && (
                <p className="text-sm text-muted-foreground flex gap-2 mb-1">
                    <Beaker className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                    <span><span className="font-semibold text-foreground">¿Cuánto? </span>{item.cuanto}</span>
                </p>
            )}
            {item.observaciones && (
                <p className="text-sm text-muted-foreground italic mt-2">{item.observaciones}</p>
            )}
            {item.enlaces?.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-2">
                    {textosDeLosEnlaces(item.enlaces, item.titulo || item.nombre).map((texto, i) => (
                        <a key={i} href={urlDeEnlace(item.enlaces[i])} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-brand hover:underline font-semibold">
                            <Link2 className="w-3 h-3" /> {texto}
                        </a>
                    ))}
                </div>
            )}
        </div>
    </div>
    );
};

/**
 * UNA LÍNEA DEL PROTOCOLO, COMO LA PIDE EL DOC DEL 16-08 (T2): el producto y, debajo,
 * «1 cacito · después de entrenar».
 *
 * La tarjeta de siempre partía la misma información en dos filas con sus rótulos
 * («¿Cuándo?» / «¿Cuánto?»), que es cómo se rellena en el panel, no cómo se lee: lo que
 * el cliente necesita saber de un vistazo es qué se toma y cuándo, en una frase.
 */
/**
 * EN QUÉ COMIDA SALE, CON SU NÚMERO (punto 184): «COMIDA 3», igual que en Inicio.
 *
 * `en_comidas` lo resuelve el servidor (`core/comida_del_suplemento`, punto 174) y viene en
 * SIMBÓLICO -- «primera», «última» -- porque quien sabe cuántas comidas hay es la pantalla que
 * tiene el día delante. Aquí no hay día, así que se usa el número de comidas que el cliente
 * tiene configurado (`GET /user/diet-config`), que es de donde sale también el reparto del
 * Inicio: si come cuatro veces, su cena es la Comida 4.
 *
 * Sin ese dato se dice «Primera comida» / «Última comida», que es verdad siempre: más vale
 * decirlo con palabras que dar un número que puede no ser el suyo.
 */
const dondeSale = (item, numComidas) => (item.en_comidas || [])
    .map((h) => {
        if (h === 'primera') return numComidas ? 'Comida 1' : 'Primera comida';
        if (h === 'ultima') return numComidas ? `Comida ${numComidas}` : 'Última comida';
        return `Comida ${String(h).replace('C', '')}`;
    })
    // Sin repetir: en un cliente de una sola comida, «desayuno y cena» son la misma.
    .filter((t, i, todas) => todas.indexOf(t) === i)
    .join(' y ');

/**
 * UN SUPLEMENTO SUYO, EN CUATRO LÍNEAS (punto 184 del 27-08):
 *
 *     Creatina
 *     COMIDA 3                       ← naranja, debajo del nombre
 *     Dosis — 5 g
 *     Cuándo — Todos los días, entrenes o no
 *     Comprar con descuento ↗
 *
 * LA COMIDA VA DESTACADA porque es lo que le hace falta a diario: «el cuándo y la dosis los
 * lee una vez y se los sabe; en qué comida va es lo que mira todos los días. Así abre la
 * pantalla y ve las tres comidas de un golpe, sin leer una línea». Y en el mismo naranja que
 * en Inicio, porque es el mismo dato.
 *
 * DOSIS Y CUÁNDO, SIN INTERROGACIÓN: «Dosis es lo que pone en los botes», y si una es etiqueta
 * la otra también. En ese orden, igual que la frase de arriba («dosis y cuándo tomarlo»).
 */
const LineaSuplemento = ({ item, numComidas }) => {
    const comida = dondeSale(item, numComidas);
    return (
        // EL DISEÑO DEL 25-10 (docs/diseño/suplementos 2): nombre grande, Dosis y Cuándo en
        // dos columnas, la observación como «Nota de tu entrenador» y los enlaces en píldoras.
        <article className="sup-mio" data-testid="supplement-card">
            {/* EL BOTE SE PINCHA (Jesús, 6-09) y lleva a su página, igual que en la guía. */}
            <ColumnaDeFotos fotos={fotosDe(item)} alt={item.titulo}
                enlacePorDefecto={urlDeEnlace(item.enlaces?.[0])} />
            <div className="flex flex-col gap-3.5 min-w-0">
                <div className="flex flex-col gap-1">
                    <h3 className="sup-mio-nombre">{item.titulo}</h3>
                    {/* En el acento, el mismo dato que verá en Inicio (punto 184). */}
                    {comida && (
                        <span className="sup-ceja" style={{ fontSize: 11, color: '#ff6a2b' }} data-testid="suplemento-comida">
                            {comida}
                        </span>
                    )}
                </div>
                {/* Dosis y luego Cuándo, el orden del punto 184. */}
                {(item.cuanto || item.cuando) && (
                    <dl className="sup-mio-dl">
                        {item.cuanto && (
                            <div className="flex flex-col gap-1"><dt>Dosis</dt><dd>{item.cuanto}</dd></div>
                        )}
                        {item.cuando && (
                            <div className="flex flex-col gap-1"><dt>Cuándo</dt><dd>{item.cuando}</dd></div>
                        )}
                    </dl>
                )}
                {item.observaciones && (
                    <div className="sup-mio-nota">
                        <span className="sup-mio-nota-titulo">Nota de tu entrenador</span>
                        <span className="sup-mio-nota-texto">{conMayuscula(item.observaciones.trim())}</span>
                    </div>
                )}
                {item.enlaces?.length > 0 && (
                    <div className="flex flex-wrap gap-2 items-center">
                        {enlacesDeMiPauta(item.enlaces, item.titulo || item.nombre).map((e, i) => (
                            <a key={i} href={e.url} target="_blank" rel="noopener noreferrer" className="sup-mio-enlace">
                                {e.texto} <span className="dto">{e.descuento ? '−20% ↗' : '↗'}</span>
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </article>
    );
};

/**
 * LA FICHA DE LA GUÍA (doc 19-08, bloque 08): Qué es · ¿Cuándo? · ¿Cuánto? y sus notas
 * sueltas, tal cual vienen de la web de Jesús.
 */
const FichaDeLaGuia = ({ ficha }) => (
    <article className="sup-ficha" data-testid="ficha-guia">
        <ColumnaDeFotos fotos={fotosDe(ficha)} alt={ficha.nombre}
            enlacePorDefecto={urlDeEnlace(ficha.enlaces?.[0])} />
        <div className="flex flex-col gap-2.5 min-w-0">
            <div className="flex flex-col gap-0.5">
                {/* EL ENCABEZADO, ENCIMA DEL NOMBRE (Jesús, 6-09): «Post-entreno
                    recomendado:». No es parte del nombre, es cuándo se toma: va como
                    rótulo, en el acento. La ficha que no lo traiga no pinta nada. */}
                {ficha.encabezado && (
                    <span className="sup-ceja" style={{ fontSize: 11 }} data-testid="encabezado-ficha">
                        {ficha.encabezado}
                    </span>
                )}
                <h3 className="sup-ficha-nombre">{ficha.nombre}</h3>
                {ficha.que_es && <span className="sup-ficha-desc">{ficha.que_es}</span>}
                {ficha.notas && <span className="sup-ficha-nota">{ficha.notas}</span>}
            </div>
            <Pautas filas={[['Cuándo', ficha.cuando], ['Dosis', ficha.cuanto]]} />
            {ficha.enlaces?.length > 0 && (
                <BotonesDeCompra enlaces={ficha.enlaces} titulo={ficha.titulo || ficha.nombre} />
            )}
            {ficha.enlaces_alternativos?.length > 0 && (
                <EnlacesAlternativos enlaces={ficha.enlaces_alternativos} />
            )}
        </div>
    </article>
);

/**
 * EL DESCUENTO, CON EL TEXTO QUE DICTÓ JESÚS EL 21-08 (apartado 10 de su doc), tal cual.
 * Antes era una línea recompuesta con los campos («GALLEGOVIP · 20 % de descuento en
 * FullGas...») y solo salía dentro de la guía: ahora el texto es el suyo, el código se
 * copia con un toque, y sale también en la pantalla del protocolo, que no lo tenía.
 */
/**
 * EL DESCUENTO EN SU PAUTA, EN UNA BANDA ARRIBA (diseño del 25-10): «−20% en toda FullGas»,
 * el código que se copia y la puerta de la tienda, antes de sus suplementos.
 */
const DescuentoEnBanda = () => {
    const [copiado, setCopiado] = useState(false);
    const copiar = async () => {
        try {
            await navigator.clipboard.writeText('GALLEGOVIP');
            setCopiado(true);
            setTimeout(() => setCopiado(false), 1800);
        } catch (e) {
            // Sin permiso de portapapeles el código sigue a la vista: no pasa nada.
            console.error('No se pudo copiar el código de descuento', e);
        }
    };
    return (
        <div className="sup-dto" data-testid="descuento-guia">
            <div className="flex flex-col gap-1 min-w-0" style={{ flex: '1 1 240px' }}>
                <span className="sup-dto-titulo">20% menos en toda la web de FullGas</span>
                <span className="sup-dto-texto">
                    Activo mientras dure tu suscripción. Empresa con la que trabajo desde hace más de 15 años.
                </span>
            </div>
            <div className="flex flex-wrap gap-2.5 items-center">
                <button onClick={copiar} type="button" title="Copiar el código" data-testid="copiar-descuento"
                    className="sup-dto-codigo">
                    <span>GALLEGOVIP</span>
                    <span style={{ fontSize: 12, letterSpacing: '.04em', color: '#ff6a2b' }}>
                        {copiado ? 'COPIADO ✓' : 'COPIAR'}
                    </span>
                </button>
                <a href="https://fullgas.org" target="_blank" rel="noopener noreferrer"
                    data-testid="ir-a-fullgas" className="sup-dto-ir">
                    Ir a FullGas ↗
                </a>
            </div>
        </div>
    );
};

const Descuento = () => {
    const [copiado, setCopiado] = useState(false);
    const copiar = async () => {
        try {
            await navigator.clipboard.writeText('GALLEGOVIP');
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
        } catch (e) {
            // Sin permiso de portapapeles el código sigue a la vista: no pasa nada.
            console.error('No se pudo copiar el código de descuento', e);
        }
    };
    return (
        <div className="sup-caja" data-testid="descuento-guia">
            <p className="sup-etiqueta mb-2">Código de descuento</p>
            <p className="text-[15px]" style={{ color: 'var(--sup-valor)' }}>
                Tienes un código especial en FullGas, la empresa con la que llevo trabajando más de 15 años.
            </p>
            <div className="my-3 flex items-center gap-2">
                <button onClick={copiar} type="button" title="Copiar el código" data-testid="copiar-descuento"
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full font-mono text-sm tracking-widest"
                    style={{ border: '1px dashed var(--sup-acento)', color: 'var(--sup-acento)' }}>
                    GALLEGOVIP
                    {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
                {copiado && <span className="sup-etiqueta" style={{ color: 'var(--sup-acento)' }}>Copiado</span>}
            </div>
            {/* «Activo TODO EL TIEMPO, mientras dure tu suscripción» decía las dos cosas: que
                es para siempre y que se acaba. Y «te aplicarán» empuja al de al lado -- es
                FullGas quien lo aplica, no nosotros --, cuando lo que hay que decir es qué
                tiene. Una sola frase, sin la contradicción y sin el tercero. */}
            <p className="text-[15px]" style={{ color: 'var(--sup-valor)' }}>
                Lo tendrás activo mientras dure tu suscripción, con un 20 % en toda la web.
            </p>
            {/* Y LA PUERTA DE LA TIENDA (Francisco, 11-09, punto 11). Se le daba el código y
                se le dejaba buscando la web: un descuento sin tienda no lo usa nadie. En
                pestaña nueva, que si no se le saca de la app en mitad de su protocolo. */}
            <a href="https://fullgas.org" target="_blank" rel="noopener noreferrer"
                data-testid="ir-a-fullgas"
                className="sup-comprar mt-3">
                Ir a FullGas <ExternalLink className="w-3.5 h-3.5" />
            </a>
        </div>
    );
};

/**
 * EL TEXTO DE ENTRADA DE LA GUÍA (25-10): es el de `CabeceraDeLaGuia`, arriba del todo.
 *
 * Hasta hoy se pintaba el `texto_entrada` del servidor, sin el «*IMPORTANTE» del descuento y
 * sin el párrafo del Catálogo Premium (punto 182 del 27-08). El texto nuevo lo dicta la
 * pantalla rediseñada, y el párrafo del Catálogo Premium sigue fuera. La guía se sigue
 * pidiendo igual; `texto_entrada` simplemente ya no se pinta aquí.
 */

const cuantosSuplementos = (n) => `${n} ${n === 1 ? 'suplemento' : 'suplementos'}`;

/**
 * UNA CATEGORÍA DE LA GUÍA (diseño del 25-10): número, nombre, cuántos tiene y su
 * explicación; al pincharla se despliega en su sitio con sus fichas. Una abierta cada vez.
 */
const CategoriaDeLaGuia = ({ seccion, numero, abierta, onToggle, subfiltro, setSubfiltro }) => {
    const fichas = subfiltro && seccion.clave === 'salud'
        ? seccion.suplementos.filter(f => (f.subfiltros || []).some(x => x.toLowerCase() === subfiltro.toLowerCase()))
        : seccion.suplementos;
    return (
        <section className={`sup-cat${abierta ? ' abierta' : ''}`}>
            <button type="button" className="sup-cat-boton" data-testid={`categoria-${seccion.clave}`}
                aria-expanded={abierta} onClick={onToggle}>
                <span className="sup-cat-num">{String(numero).padStart(2, '0')}</span>
                <span className="flex flex-col gap-1 min-w-0">
                    <span className="flex items-baseline gap-3 flex-wrap">
                        <span className="sup-cat-nombre">{seccion.nombre}</span>
                        <span className="sup-cat-cuantos">{cuantosSuplementos(seccion.suplementos.length)}</span>
                    </span>
                    {seccion.explicacion && <span className="sup-cat-desc">{seccion.explicacion}</span>}
                </span>
                <span className="sup-chevron" aria-hidden="true"><ChevronDown className="w-5 h-5" /></span>
            </button>
            <div className="sup-cat-cuerpo" aria-hidden={!abierta}>
                <div>
                    {/* Los seis apartados de Salud, solo dentro de Salud. */}
                    {seccion.clave === 'salud' && seccion.subfiltros?.length > 0 && (
                        <div className="flex flex-wrap gap-2 px-6 pb-3 max-sm:px-3">
                            {seccion.subfiltros.map(sf => (
                                <button key={sf} type="button"
                                    onClick={() => setSubfiltro(subfiltro === sf ? null : sf)}
                                    className={`sup-chip${subfiltro === sf ? ' activo' : ''}`}>
                                    {sf}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="sup-cat-fichas">
                        {fichas.map(f => <FichaDeLaGuia key={f.id} ficha={f} />)}
                    </div>
                </div>
            </div>
        </section>
    );
};

/**
 * LA GUÍA (doc 19-08, presentada como dictó Jesús el 21-08): primero LAS CATEGORÍAS, para
 * pinchar y abrir, no todo el mensaje de golpe. Desde el 25-10 se abren en su sitio, como en
 * el diseño, en vez de cambiar de vista. El remate por plan sigue igual (el aviso del plan
 * personalizado o la oferta de los 87).
 */
const GuiaDeSuplementacion = ({ api, guia, conDescuento = true, alYaEstarPagado }) => {
    // SIETE CATEGORÍAS Y NINGÚN CAJÓN DE SASTRE (punto 187 del 27-08): una ficha sin
    // categoría se queda fuera de la guía hasta que se le ponga la suya.
    const entradas = (guia?.secciones || []).filter(s => s.suplementos.length > 0);
    // La que se abre al entrar es la de los básicos: «te recomiendo empezar con los básicos».
    const [abierta, setAbierta] = useState(() => (entradas.some(s => s.clave === 'basicos') ? 'basicos' : null));
    const [subfiltro, setSubfiltro] = useState(null);
    const [pidiendo, setPidiendo] = useState(false);
    const { refreshProfile } = useAuth();

    if (!guia) return null;

    const pedirRevision = async () => {
        setPidiendo(true);
        try {
            const { url } = await abrirPagoDelAjuste(api);
            if (url) { window.location.href = url; return; }
            toast.success(AJUSTE_YA_EN_MARCHA, { siempre: true });
            await Promise.all([refreshProfile(), alYaEstarPagado?.()]);
        } catch (e) {
            console.error('[oferta 87] no se pudo abrir el checkout', e);
            toast.error('No hemos podido abrir el pago. Inténtalo en un momento.');
        } finally {
            setPidiendo(false);
        }
    };

    return (
        <section className="flex flex-col gap-8" data-testid="guia-suplementacion">
            {/* EL AVISO DEL QUE ESTÁ ESPERANDO (punto 183 del 27-08): qué pasa y qué va a
                pasar, sin fecha. */}
            {guia.aviso_plan_personalizado && (
                <div className="sup-caja acento" data-testid="aviso-guia-basica">
                    <p className="text-[15px] font-medium">
                        Te lo estamos preparando. Te avisamos en cuanto esté.
                    </p>
                </div>
            )}

            <div className="flex flex-col gap-3">
                {entradas.map((s, i) => (
                    <CategoriaDeLaGuia key={s.clave} seccion={s} numero={i + 1}
                        abierta={abierta === s.clave}
                        onToggle={() => { setAbierta(abierta === s.clave ? null : s.clave); setSubfiltro(null); }}
                        subfiltro={subfiltro} setSubfiltro={setSubfiltro} />
                ))}
            </div>

            {/* El descuento, con el texto de Jesús. En la pantalla del protocolo lo pinta
                la propia pantalla junto a la pauta, y aquí se apaga para no repetirlo. */}
            {conDescuento && <Descuento />}

            {/* LA OFERTA, AL FINAL Y SOLO A QUIEN NO LO TIENE (doc 19-08): «entra a
                consultar qué tomar y lo último que ve es lo que le falta». El botón va al
                mismo checkout que la oferta del final del alta. */}
            {guia.oferta_87 && (
                <div className="sup-caja acento" data-testid="oferta-87-guia">
                    <p className="sup-h2 mb-2" style={{ fontSize: 22 }}>¿Quieres tu plan de suplementación personalizado?</p>
                    <p className="sup-entrada mb-4" style={{ fontSize: 15 }}>
                        Va incluido con la revisión de tus macros: te los ajustamos a medida
                        y te preparamos tu suplementación con ellos.
                    </p>
                    <button type="button" onClick={pedirRevision} disabled={pidiendo} data-testid="solicitar-revision-87"
                        className="sup-comprar disabled:opacity-50" style={{ border: 0, cursor: 'pointer' }}>
                        {pidiendo ? 'Abriendo el pago...' : 'Solicitar la revisión de mis macros · 87 €'}
                    </button>
                </div>
            )}
        </section>
    );
};

const SupplementsPage = () => {
    const { api, pantalla } = useAuth();
    // El interruptor del panel (doc 16-08): encendido -- que es como nace -- la pantalla
    // habla como el documento; apagado se queda la de siempre, sin desplegar nada.
    const nuevo = pantalla('t2_suplementos', true);
    const [protocol, setProtocol] = useState(null);
    const [guia, setGuia] = useState(null);
    // Cuantas comidas tiene puesto, para decir «Comida 4» y no «ultima comida» (punto 184).
    const [numComidas, setNumComidas] = useState(null);
    const [loading, setLoading] = useState(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchProtocol(); }, []);

    const fetchProtocol = async () => {
        try {
            const res = await api.get('/supplements/current');
            setProtocol(res.data);
        } catch (e) {
            console.error('Error fetching supplements:', e);
        }
        // La guía la ven todos, tenga o no protocolo; si falla, la pantalla sigue con lo
        // que haya.
        // El numero de comidas del cliente: si falla, la comida se dice con palabras.
        try {
            const c = await api.get('/user/diet-config');
            setNumComidas(c.data?.num_comidas || null);
        } catch { /* se queda en palabras */ }
        try {
            const g = await api.get('/supplements/guia');
            setGuia(g.data);
        } catch (e) {
            console.error('No se pudo traer la guía de suplementación:', e);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Wrap><div className="animate-pulse space-y-4">
            <div className="h-9 bg-muted rounded w-1/3" />
            <div className="h-24 bg-muted rounded-2xl" />
            <div className="h-24 bg-muted rounded-2xl" />
        </div></Wrap>;
    }

    return <VistaDeSuplementos api={api} protocol={protocol} guia={guia}
        numComidas={numComidas} nuevo={nuevo} alYaEstarPagado={fetchProtocol} />;
};

/**
 * LO QUE SE PINTA, APARTE DE LO QUE SE PIDE (hoja del 21-10, fila 32: «Así verá la
 * suplementación el cliente» en su ficha). La pantalla del cliente pide sus datos como
 * cliente; la ficha los pide para él (`/admin/supplements/vista-cliente`, que los resuelve
 * con las mismas funciones del servidor) y pinta ESTO, la misma pantalla, no una copia.
 */
export const VistaDeSuplementos = ({ api, protocol, guia, numComidas, nuevo, alYaEstarPagado }) => {
    const tieneActual = protocol?.actual?.length > 0;
    const tieneSiguiente = protocol?.siguiente?.length > 0;
    // La general compuesta murió con el doc 19-08: sin protocolo propio, lo que se ve es
    // LA GUÍA entera de Jesús.
    const esGenerica = !!protocol?.es_generica;
    const hayGuia = (guia?.secciones || []).some(s => s.suplementos.length > 0) || (guia?.sin_seccion || []).length > 0;

    /**
     * LOS TRES ESTADOS DE ESTA PANTALLA (puntos 179 y 180 del 27-08).
     *
     *   Con plan y con protocolo   →  «Mis suplementos». Sólo lo suyo. LA GUÍA NO SALE.
     *   Con plan, sin protocolo    →  «Mis suplementos», el aviso de que se lo estás
     *                                 preparando, y la guía debajo para que tenga algo.
     *   Sin plan personalizado     →  «Suplementación»: la guía, y nada más.
     *
     * Hasta hoy eran dos y ninguno de los tres: quien ya tenía su pauta veía ADEMÁS la guía
     * entera debajo, y los dos casos se llamaban «Tu suplementación», que es lo que denuncia
     * el 180 -- «en uno de los dos es mentira». El «mis» es lo que dice que son suyos.
     *
     * Y de qué estado se trata lo dice el SERVIDOR (`con_plan`), no el código de error de la
     * petición del protocolo: antes se distinguía porque `/supplements/current` devuelve 403 a
     * quien no tiene el plan, y eso funcionaba de casualidad.
     */
    const conPlan = !!guia?.con_plan;

    if (!tieneActual && !tieneSiguiente && !protocol?.nota && hayGuia) {
        // LA GUÍA, CON SU CABECERA DEL 25-10: ceja «Mis suplementos», título «Guía básica de
        // suplementación» y el texto de Jesús justo antes de las categorías, tenga o no plan.
        return <Wrap>
            <CabeceraDeLaGuia />
            {/* EL DESCUENTO ARRIBA TAMBIÉN AQUÍ (27-10), con la banda de quien tiene su
                pauta; abajo, dentro de la guía, se apaga para no repetirlo. */}
            <DescuentoEnBanda />
            <GuiaDeSuplementacion api={api} guia={guia} conDescuento={false} alYaEstarPagado={alYaEstarPagado} />
        </Wrap>;
    }

    if (!protocol || (!tieneActual && !tieneSiguiente && !protocol.nota)) {
        return <Wrap>
            {/* AQUÍ SE HABÍA QUEDADO «TU SUPLEMENTACIÓN» (30-08). El punto 180 la quitó de
                las otras dos pantallas -- «Mis suplementos» cuando es suyo, «Suplementación»
                cuando es la guía --, pero esta tercera, la de cuando no hay ni protocolo ni
                guía que enseñar, seguía con el nombre viejo. Y encima decidía con `nuevo`,
                que es el interruptor de la pantalla, no si el cliente tiene plan: por eso no
                se vio. Misma señal que arriba, `conPlan`. */}
            <Cabecera conPlan={conPlan} />
            <div className="sup-caja text-center" style={{ padding: '40px 24px' }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ background: '#26231f', color: 'var(--sup-acento)' }}>
                    <Pill className="w-8 h-8" />
                </div>
                {/* YA NO HAY NADA QUE TRAER (punto 4.15, cerrado el 09-08-2026). Este texto
                    decía «lo tienes en el sistema anterior y lo estamos trayendo», que era
                    verdad mientras los protocolos seguían en Calma. Ya no: se migraron los 98
                    clientes que tenían uno, con su histórico entero. Quien llegue aquí ahora es
                    porque de verdad no tiene protocolo puesto, y hay que decírselo tal cual en
                    vez de hacerle esperar algo que no va a llegar.

                    El texto anterior hablaba además de 666 clientes pendientes: un dato interno
                    nuestro que no le decía nada a quien lo leía. */}
                {/* NI «PROTOCOLO» NI «TU ENTRENADOR» (#53 del 15-08: «sigue el lenguaje de
                    catálogo»). «Protocolo» es como lo llamamos nosotros por dentro; el
                    cliente compró que le digamos qué tomar. Y «tu entrenador» le habla de
                    una persona que casi nadie tiene asignada en la app: aquí quien contesta
                    es el equipo. */}
                {/* AQUÍ SOLO SE LLEGA SI NO HAY NI GENERAL (18-08). Este cartel era lo que
                    veía todo el que no tuviera la suya escrita, y es justo lo que Jesús no
                    quiere: mientras no le pongamos la suya, se le enseña la general. El
                    servidor la manda siempre que el catálogo tenga base o intra, así que
                    esto ya solo sale si el catálogo está vacío. */}
                {/* Y FUERA EL CHAT TAMBIÉN AQUÍ (punto 180). El título ya se arregló esta
                    mañana; lo que seguía era este texto, que le manda a una puerta que puede
                    no ser suya -- el chat solo lo llevan el Gold y el Premium -- y le pide
                    que sea él quien reclame. Si se lo estamos preparando nosotros, no hay
                    nada que reclamar: se le dice qué espera y que estamos en ello.

                    «Tu plan de suplementación personalizado» y no «suplementación» a secas:
                    a quien tiene plan se le está preparando el suyo, y esa es la diferencia
                    que el 179 pide que se note. */}
                <h2 className="sup-h2" style={{ fontSize: 22 }}>
                    {conPlan ? 'Todavía no tienes tu plan de suplementación personalizado'
                        : 'Todavía no tienes suplementación'}
                </h2>
            </div>
        </Wrap>;
    }

    const Tarjeta = nuevo ? LineaSuplemento : SupplementCard;

    return (
        <Wrap mia>
            {/* «MIS SUPLEMENTOS», CON EL MIS (punto 180): son suyos. La guía se llama
                «Suplementación», sin el mis, porque es la guía y no la suya. Hoy se llamaban
                las dos igual y en una de las dos era mentira. */}
            {/* Y LA SEÑAL ES EL PLAN, NO EL INTERRUPTOR (Gonzalo, 4-09, puntos 81/82 y 85;
                cerrado el 13-09). Esta tercera pantalla -- la del que SÍ tiene su pauta -- era
                la única que seguía decidiendo el título con `nuevo`, que es el interruptor de
                `pantalla('t2_suplementos')` del panel y no dice nada del cliente. Las otras
                dos ya se arreglaron el 30-08 y quedó escrito ahí arriba («Misma señal que
                arriba, `conPlan`»); esta se quedó atrás. Con el interruptor apagado, quien
                tiene su protocolo personalizado leía «SUPLEMENTACIÓN» encima de sus propios
                suplementos, que es justo la mentira que denuncia el 180. */}
            <header className="flex flex-col gap-2.5">
                {/* La ceja del diseño del 25-10; la pauta de base no es de ningún entrenador. */}
                <span className="sup-ceja">{esGenerica ? 'Mis suplementos' : 'Plan asignado por tu entrenador'}</span>
                <h1 className="sup-titulo">{conPlan ? 'Mis suplementos' : 'Suplementación'}</h1>
                {/* EL SUBTÍTULO ES SUYO, NO UN DESCARGO: esto no es un catálogo, es lo que le
                    ha pautado a él (punto 181). */}
                {(esGenerica || !nuevo) && (
                    <p className="sup-entrada mt-1">
                        {esGenerica
                            ? 'Todavía no te he pautado la tuya. Mientras tanto, esta es la que recomiendo de base: en cuanto te ponga la tuya, la ves aquí.'
                            : 'Aquí ves algunos de los suplementos más habituales que recomiendo, así como su modo de empleo. Esta información es orientativa: pueden ser necesarios otros suplementos o dosis según tu situación, objetivos o tolerancias.'}
                    </p>
                )}
            </header>

            {/* EL DESCUENTO, EN LA PANTALLA DEL PROTOCOLO (Jesús, 21-08), y desde el 25-10
                arriba, antes de sus suplementos, como en el diseño. */}
            <DescuentoEnBanda />

            {protocol.nota && (
                <div className="sup-caja acento flex gap-3">
                    <StickyNote className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--sup-acento)' }} />
                    <div>
                        <p className="sup-ceja mb-1" style={{ fontSize: 11 }}>Nota personal</p>
                        <p className="text-[15px] leading-relaxed" style={{ color: 'var(--sup-valor)' }}>{protocol.nota}</p>
                    </div>
                </div>
            )}

            {tieneActual && (
                <section className="flex flex-col gap-3">
                    {/* Sin rótulo cuando no hay nada más: la pantalla entera ya dice qué es
                        esto, y «Suplementación actual» solo hace falta para distinguirla de
                        la que entra más adelante. */}
                    {(!nuevo || tieneSiguiente) && (
                        <h2 className="sup-h2">
                            {esGenerica ? 'La suplementación general' : 'Suplementación actual'}
                        </h2>
                    )}
                    <div className="grid gap-3">
                        {protocol.actual.map((it, i) => <Tarjeta key={i} item={it} numComidas={numComidas} />)}
                    </div>
                </section>
            )}

            {tieneSiguiente && (
                <section className="flex flex-col gap-3">
                    <div className="flex items-baseline gap-3 flex-wrap">
                        <h2 className="sup-h2">Suplementación siguiente</h2>
                        {protocol.siguiente_fecha && (
                            <span className="sup-ceja inline-flex items-center gap-1.5" style={{ fontSize: 11 }}>
                                <CalendarClock className="w-3.5 h-3.5" /> A partir del {formatDate(protocol.siguiente_fecha)}
                            </span>
                        )}
                    </div>
                    <div className="grid gap-3">
                        {protocol.siguiente.map((it, i) => <Tarjeta key={i} item={it} numComidas={numComidas} />)}
                    </div>
                </section>
            )}

            {/* AQUÍ IBA LA GUÍA ENTERA, Y SE VA (punto 179 del 27-08): «con plan y con
                protocolo puesto → Mis suplementos: SÓLO LO SUYO. La guía no sale».
                El doc del 19-08 decía «la ven todos», y por eso estaba; pero el que ya tiene
                su pauta no viene aquí a leer un catálogo, viene a mirar qué le toca hoy, y
                debajo de tres líneas suyas había veintiocho fichas que no son suyas.
                Quien no tiene pauta sí la sigue viendo: es el segundo estado, arriba. */}
        </Wrap>
    );
};

export default SupplementsPage;
